import { useState, useRef, useEffect } from "react";
import { FiWifi } from "react-icons/fi";
import { FaMicrochip } from "react-icons/fa6";

const WalletCardVisaLike = ({
  isDarkMode,
  displayBalance,
  currency,
  loading,
  redeemCode,
  setRedeemCode,
  handleRedeem,
  glow,
  schoolLogo = "/school-logo.png",
  cardHolderInfo,
}) => {
  const [flipped, setFlipped] = useState(false);

  const SEGMENT_COUNT = 3;
  const SEGMENT_LEN = 5;
  const [segments, setSegments] = useState(Array(SEGMENT_COUNT).fill(""));
  const inputsRef = useRef([]);

  // Update segments when redeemCode changes externally
  useEffect(() => {
    if (!redeemCode) {
      setSegments(Array(SEGMENT_COUNT).fill(""));
      return;
    }
    const normalized = redeemCode
      .replace(/\s+/g, "")
      .slice(0, SEGMENT_COUNT * SEGMENT_LEN);
    const s = [];
    for (let i = 0; i < SEGMENT_COUNT; i++) {
      s.push(normalized.slice(i * SEGMENT_LEN, (i + 1) * SEGMENT_LEN));
    }
    setSegments(s);
  }, [redeemCode]);

  // Format balance nicely
  const formatBalance = (val) => {
    const num = Number(val || 0);
    const formatted =
      Math.abs(Math.round(num * 100) % 100) === 0
        ? new Intl.NumberFormat().format(Math.round(num))
        : new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(Number(num.toFixed(2)));
    return `${formatted} ${currency ?? ""}`;
  };

  // Handle segmented input change
  const onSegmentChange = (idx, raw) => {
    const val = raw
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, SEGMENT_LEN);
    const next = [...segments];
    next[idx] = val;
    setSegments(next);
    const combined = next.join("");
    setRedeemCode(combined);

    // Auto-advance
    if (val.length === SEGMENT_LEN && idx < SEGMENT_COUNT - 1) {
      inputsRef.current[idx + 1]?.focus();
    }

    // Auto-submit
    if (combined.length === SEGMENT_COUNT * SEGMENT_LEN) {
      setTimeout(() => {
        handleRedeem();
        setFlipped(false);
      }, 250);
    }
  };

  // Support paste into first input
  const onPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData("text");
    const normalized = pasted
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, SEGMENT_COUNT * SEGMENT_LEN);
    const s = [];
    for (let i = 0; i < SEGMENT_COUNT; i++) {
      s.push(normalized.slice(i * SEGMENT_LEN, (i + 1) * SEGMENT_LEN));
    }
    setSegments(s);
    setRedeemCode(s.join(""));
    const firstEmpty = s.findIndex((seg) => seg.length < SEGMENT_LEN);
    const focusIdx = firstEmpty === -1 ? SEGMENT_COUNT - 1 : firstEmpty;
    setTimeout(() => inputsRef.current[focusIdx]?.focus(), 50);

    if (normalized.length === SEGMENT_COUNT * SEGMENT_LEN) {
      setTimeout(() => {
        handleRedeem();
        setFlipped(false);
      }, 250);
    }
  };

  // Flip back to front
  const flipToFront = () => setFlipped(false);

  const Chip = () => (
    <div className="w-12 h-9 rounded-md bg-gradient-to-b from-yellow-300 to-yellow-500 flex items-center justify-center text-xs font-semibold text-black shadow-inner">
      <FaMicrochip />
    </div>
  );

  return (
    <div
      className={`w-full min-w-0 h-[280px] sm:h-[250px] rounded-2xl shadow-2xl border perspective-1000 ${
        glow
          ? isDarkMode
            ? "border-red-500 shadow-[0_0_20px_#ef4444]"
            : "border-blue-500 shadow-[0_0_20px_#3b82f6]"
          : isDarkMode
          ? "border-gray-700"
          : "border-gray-300"
      }`}
    >
      <div
        className={`card-inner w-full h-full relative transition-transform duration-500 [transform-style:preserve-3d] ${
          flipped ? "flipped" : ""
        }`}
        style={{ transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}
      >
        {/* Front Face */}
        <div
          className={`card-front absolute inset-0 rounded-2xl p-4 sm:p-6 flex flex-col justify-between [backface-visibility:hidden] ${
            isDarkMode
              ? "bg-gradient-to-r from-[#1f1f1f] to-[#2a2a2a] text-white"
              : "bg-gradient-to-r from-blue-600 to-blue-500 text-white"
          }`}
        >
          {/* Top */}
          <div className="flex justify-between items-start">
            <img
              src={schoolLogo}
              alt="School Logo"
              className="w-[120px] h-auto object-contain"
            />
            <div className="flex flex-col items-end">
              <FiWifi className="text-white text-xl opacity-85" />
              <button
                onClick={() => setFlipped(true)}
                className="mt-2 text-xs bg-white/20 backdrop-blur-sm px-3 py-1 rounded-md hover:bg-white/30"
                aria-label="Flip card to redeem"
                title="Flip card to redeem"
              >
                Redeem
              </button>
            </div>
          </div>

          {/* Middle */}
          <div className="flex items-center gap-4 mt-4">
            <Chip />
            <div className="flex-1">
              <div className="text-xs opacity-90">Card Holder</div>
              <div className="text-sm font-semibold emboss">
                {cardHolderInfo?.name}
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs opacity-80">Current Balance</div>
              <div className="text-xl sm:text-2xl font-bold emboss break-words">
                {loading ? (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce-sm delay-0"></span>
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce-sm delay-200"></span>
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce-sm delay-400"></span>
                  </div>
                ) : (
                  formatBalance(displayBalance)
                )}
              </div>
            </div>
            <div className="text-right font-mono text-sm opacity-95">
              <div className="mb-1 tracking-widest">
                {cardHolderInfo?.school}
              </div>
              <div className="text-xs opacity-80">
                {cardHolderInfo?.academicLevel}
              </div>
            </div>
          </div>
        </div>

        {/* Back Face */}
        <div
          className={`card-back absolute inset-0 rounded-2xl p-4 sm:p-6 flex flex-col justify-between [backface-visibility:hidden] [transform:rotateY(180deg)] ${
            isDarkMode ? "bg-[#111214] text-white" : "bg-gray-50 text-gray-900"
          }`}
        >
          {/* Magnetic stripe look */}
          <div
            className={`${
              isDarkMode ? "bg-gray-800" : "bg-black"
            } h-10 rounded-sm mb-4`}
          ></div>

          {/* Redeem Inputs */}
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div className="mb-1 text-sm opacity-80">
              Enter 15‑char code (5-5-5)
            </div>
            <div className="flex w-full min-w-0 gap-2">
              {segments.map((seg, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputsRef.current[idx] = el)}
                  value={seg}
                  onChange={(e) => onSegmentChange(idx, e.target.value)}
                  onPaste={idx === 0 ? onPaste : undefined}
                  className={`w-0 min-w-0 flex-1 text-center p-2 rounded-md border focus:outline-none font-mono tracking-widest ${
                    isDarkMode
                      ? "bg-[#0f1112] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-black"
                  }`}
                  maxLength={SEGMENT_LEN}
                  inputMode="text"
                />
              ))}
            </div>
            <div className="text-xs opacity-70 mt-2 pb-3">
              Tip: paste full code to auto-fill
            </div>
          </div>

          {/* Bottom actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setSegments(Array(SEGMENT_COUNT).fill(""));
                setRedeemCode("");
                inputsRef.current[0]?.focus();
              }}
              className="text-sm px-3 py-1 rounded-md bg-transparent border border-current/10"
            >
              Clear
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={flipToFront}
                className={`${
                  isDarkMode
                    ? "bg-transparent border border-customRed hover:bg-customRed transition-all duration-300"
                    : "bg-gray-200"
                } px-3 py-1 rounded-md text-sm`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const combined = segments.join("");
                  if (combined.length !== SEGMENT_COUNT * SEGMENT_LEN) return;
                  handleRedeem(combined);
                  setFlipped(false);
                }}
                disabled={
                  segments.join("").length !== SEGMENT_COUNT * SEGMENT_LEN ||
                  loading
                }
                className={`px-3 py-1 rounded-md text-sm font-semibold ${
                  isDarkMode
                    ? "bg-red-600 text-white disabled:opacity-50"
                    : "bg-blue-600 text-white disabled:opacity-50"
                }`}
              >
                {loading ? "Processing..." : "Redeem"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletCardVisaLike;
